import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { readWorkspace } from '../lib/workspaceStorage';
import { apiErrorMessage } from '../lib/apiError';

// Empty shells are hidden, never deleted. Browser-only drafts remain reachable.
const visibleItem = (item, owner) => {
  const draft = readWorkspace(`chat-draft:${owner}${item.id === 'legacy' ? '' : ':' + item.id}`, '');
  const pending = readWorkspace(`chat-request:${owner}:${item.id}`);
  if (item.is_empty !== true || pending) return item;
  if (typeof draft === 'string' && draft.trim()) return { ...item, title: draft.trim().slice(0, 80), localDraft: true };
  return null;
};

async function visiblePage(api, owner, query, offset, isCurrent) {
  // Skip empty-only pages without confusing an empty first page with no history.
  // Bound each request group; the remaining cursor stays available via More.
  for (let page = 0; page < 5; page += 1) {
    const { data } = await axios.get(`${api}/conversations`, { params: { q: query, ...(offset != null ? { offset } : {}) } });
    if (!isCurrent()) return null;
    const items = (data.items || []).map(item => visibleItem(item, owner)).filter(Boolean);
    const next = data.next_offset ?? null;
    if (items.length || next === null || page === 4 || next === offset) return { items, next };
    offset = next;
  }
}

export default function ConversationHistory({ open, owner, api, currentId, disabled, onClose, onSelect, onDeleted, messages = [], onJump }) {
  const dialog = useRef(null);
  const [query, setQuery] = useState(''), [items, setItems] = useState([]);
  const [next, setNext] = useState(null), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const generation = useRef(0);
  const [removing, setRemoving] = useState(null), [deleting, setDeleting] = useState(false);
  useEffect(() => { setRemoving(null); }, [open, owner]);
  const remove = async () => {
    if (!removing || deleting || disabled) return;
    const version = generation.current; setDeleting(true); setError('');
    try {
      await axios.delete(`${api}/conversations/${encodeURIComponent(removing.id)}`, { params: { version: removing.version } });
      if (version !== generation.current) return;
      const id = removing.id; setRemoving(null); setRetry(value => value + 1); onDeleted?.(id);
    } catch (e) { if (version === generation.current) setError(apiErrorMessage(e, 'Sohbet silinemedi; tekrar deneyebilirsin.')); }
    finally { setDeleting(false); }
  };
  useEffect(() => { setQuery(''); setItems([]); setNext(null); setError(''); }, [owner]);
  useEffect(() => {
    if (open && !dialog.current.open) dialog.current.showModal();
    if (!open && dialog.current.open) dialog.current.close();
  }, [open]);
  useEffect(() => {
    const version = ++generation.current;
    if (!open) return undefined;
    setBusy(true); setError('');
    const timer = setTimeout(() => {
      visiblePage(api, owner, query, null, () => generation.current === version).then(data => {
        if (generation.current !== version) return;
        setItems(data.items); setNext(data.next);
      }).catch(e => { if (generation.current === version) setError(apiErrorMessage(e, 'Sohbetlerin yüklenemedi.')); })
        .finally(() => { if (generation.current === version) setBusy(false); });
    }, query ? 250 : 0);
    return () => { clearTimeout(timer); generation.current += 1; };
  }, [open, owner, query, api, retry]);
  const more = async () => {
    const version = generation.current; setBusy(true); setError('');
    try {
      const data = await visiblePage(api, owner, query, next, () => generation.current === version);
      if (version === generation.current) { setItems(old => [...old, ...data.items].filter((item, index, all) => all.findIndex(other => other.id === item.id) === index)); setNext(data.next); }
    } catch (e) { if (version === generation.current) setError(apiErrorMessage(e, 'Diğer sohbetler yüklenemedi.')); }
    finally { if (version === generation.current) setBusy(false); }
  };
  return <dialog ref={dialog} className="chat-tool-dialog chat-conversations-dialog" aria-label="Sohbetlerim" onCancel={e => { e.preventDefault(); if (!deleting) onClose(); }}>
    <div className="chat-tool-header"><h2>Sohbetlerim</h2><button className="action-secondary" disabled={deleting} onClick={onClose}>Kapat</button></div>
    {messages.some(m => m.role === 'user') && <details className="chat-current-messages"><summary>Bu sohbetteki mesajlar</summary>{messages.map((m, index) => m.role === 'user' && <button className="text-action" key={index} onClick={() => onJump?.(index)}>{m.text}</button>)}</details>}
    <label className="chat-history-search">Sohbet başlığı veya proje ara<input type="search" disabled={deleting} value={query} onChange={e => setQuery(e.target.value)} maxLength={100} placeholder="Örneğin sushi atölyesi" /></label>
    {error && <div role="alert"><p>{error}</p><button className="action-secondary" disabled={deleting} onClick={() => setRetry(value => value + 1)}>Tekrar dene</button></div>}
    {busy && <p role="status">Sohbetlerin yükleniyor…</p>}
    {!busy && !error && !items.length && next === null && <p className="chat-history-empty">{query ? 'Bu aramayla eşleşen sohbet yok.' : 'Yeni sohbetlerin burada saklanacak.'}</p>}
    {removing && <section className="chat-confirm" aria-label="Sohbet silme onayı"><h3>“{removing.title}” silinsin mi?</h3><p>Sohbet mesajları kalıcı olarak silinir; bu sekmedeki gönderilmemiş taslak temizlenir. Kayıtlı proje, başvuru ve profil bilgilerin korunur. Bu işlem geri alınamaz.</p><button className="action-secondary" disabled={deleting} onClick={() => setRemoving(null)}>Vazgeç</button><button className="action-secondary" disabled={deleting || disabled} onClick={remove}>{deleting ? 'Siliniyor…' : 'Kalıcı olarak sil'}</button></section>}
    <ul className="chat-conversations-list">{items.map(item => <li key={item.id}><button disabled={disabled || busy || deleting} aria-current={item.id === currentId ? 'true' : undefined} onClick={() => onSelect(item.id)}>
      <strong>{item.title}</strong><span>{item.project?.title ? `Proje: ${item.project.title}` : item.localDraft ? 'Gönderilmemiş taslak · Bu sekmede' : 'Bağımsız çalışma'}{item.updated_at ? ` · ${new Date(item.updated_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}` : ''}</span>
      {item.pending_status && <small>{item.pending_status === 'executing' ? 'İşlem sonucu kontrol edilmeli' : 'Onay bekleyen taslak'}</small>}
    </button><button className="chat-delete-button" aria-label={`${item.title} sohbetini sil`} disabled={disabled || busy || deleting || item.version == null} onClick={() => { setRemoving(item); setError(''); }}>Sil</button></li>)}</ul>
    {next !== null && <button className="action-secondary" disabled={busy || deleting} onClick={more}>Daha fazla sohbet</button>}
  </dialog>;
}
